import { useCallback, useEffect, useState } from 'react'
import { X, Loader2, Check, Lock, ExternalLink, Info, MessageSquareQuote } from 'lucide-react'
import { apiGet, patchJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { useMe } from '../../lib/me.js'
import { isHR } from '../../lib/role.js'
import { fullName } from '../../lib/employee.js'
import { useEmployeeDrawer } from './EmployeeDrawer.jsx'
import { resignLabel, resignTone, lastDayLabel, noticeGiven, NOTICE_DAYS } from '../../lib/resignation.js'
import { fmtDay, fmtStamp } from '../../lib/warning.js'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * The resignation drawer — review, accept, and (for HR only) read the off-boarding
 * answers.
 *
 * 🚨 ACCEPTING DOES NOT WRITE Employee.date_end. That column gates payroll and the
 * roster, so an acceptance in August silently ending an employment record on a
 * future date — with a withdrawal then needing undoing in two places — is exactly
 * what this avoids. Instead the drawer offers a link into the employee editor with
 * the date to enter, and a human presses save. Navigation, not action: the same
 * doctrine as ขอเวร's roster deep-link, and as "approving leave never mutates the
 * roster" on the doctor side.
 *
 * 🔒 The survey block below is fetched ONLY for HR/Admin, and the fetch itself is
 * refused by both the middleware and Xano for anyone else. A Manager sees the
 * padlock and a sentence — not an empty panel that reads as "they said nothing".
 */
export default function ResignationDrawer({ resignation, employee, onClose, onSaved }) {
  const openEmployee = useEmployeeDrawer()
  const me = useMe()
  const hr = isHR(me)
  const r = resignation

  const [note, setNote] = useState(r.decision_note || '')
  const [lastDay, setLastDay] = useState(r.last_working_date?.slice(0, 10) || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [confirm, setConfirm] = useState(null)

  const [survey, setSurvey] = useState(null)
  const [surveyLoading, setSurveyLoading] = useState(hr)
  const [surveyErr, setSurveyErr] = useState('')

  const loadSurvey = useCallback(async () => {
    if (!hr) return
    setSurveyLoading(true)
    setSurveyErr('')
    try {
      setSurvey(await apiGet(`/api/hr/resignation-survey/${r.id}`))
    } catch (e) {
      setSurveyErr(apiErrorText(e, 'โหลดแบบสอบถามไม่สำเร็จ'))
    } finally {
      setSurveyLoading(false)
    }
  }, [hr, r.id])

  useEffect(() => { loadSurvey() }, [loadSurvey])

  const notice = noticeGiven(r.filed_at || r.created_at, r.last_working_date)

  async function accept() {
    setConfirm(null)
    setBusy(true)
    setErr('')
    try {
      await patchJson(`/api/hr/resignation/${r.id}`, {
        status: 'accepted',
        decision_note: note.trim(),
        last_working_date: lastDay || undefined,
      })
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  async function markApplied() {
    setBusy(true)
    setErr('')
    try {
      await patchJson(`/api/hr/resignation/${r.id}`, { applied_to_employee: true })
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 ${resignTone(r.status)}`}>
                {resignLabel(r.status)}
              </span>
            </div>
            <h2 className="mt-1.5 text-[16px] font-semibold text-slate-900 truncate">
              {employee?.code || `#${r.employee_id}`}
              {employee && <span className="ml-2 text-[13px] font-normal text-slate-500">{fullName(employee)}</span>}
            </h2>
            <p className="text-[12.5px] text-slate-500">
              แจ้งเมื่อ {fmtStamp(r.filed_at || r.created_at)}
              {notice != null && <> · แจ้งล่วงหน้า {notice} วัน</>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11.5px] text-slate-400">{lastDayLabel(r)}</div>
              <div className="text-[15px] font-semibold text-slate-800 tabular-nums">{fmtDay(r.last_working_date)}</div>
            </div>
            <div>
              <div className="text-[11.5px] text-slate-400">แจ้งล่วงหน้า</div>
              <div className={`text-[15px] font-semibold tabular-nums ${notice != null && notice < NOTICE_DAYS ? 'text-amber-600' : 'text-slate-800'}`}>
                {notice == null ? '—' : `${notice} วัน`}
              </div>
            </div>
          </div>

          {r.reason_short && (
            <div>
              <div className="text-[11.5px] font-medium text-slate-400 mb-1">เหตุผลโดยย่อ</div>
              <p className="text-[13.5px] text-slate-700 break-words">{r.reason_short}</p>
            </div>
          )}

          {/* 🚨 The one thing this drawer must not do silently. */}
          {r.status === 'accepted' && !r.applied_to_employee && (
            <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5">
              <div className="flex gap-2">
                <Info size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[12.5px] text-amber-800 leading-relaxed">
                  ยังไม่ได้บันทึก <b>วันสิ้นสุดการจ้าง</b> ในประวัติพนักงาน — ระบบไม่เขียนให้อัตโนมัติ
                  เพราะช่องนี้มีผลกับเงินเดือนและตารางเวร
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {/* Was a bare href to /list — a full page reload that landed HR
                        on 380 rows with the person to find again, which is how "do it
                        later" happened. Opens the record itself now, over this drawer;
                        closing it comes back here to press บันทึกแล้ว. */}
                    <button
                      type="button"
                      onClick={() => openEmployee?.(r.employee_id)}
                      disabled={!openEmployee}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white ring-1 ring-amber-300 text-amber-800 text-[12px] font-medium hover:bg-amber-100 disabled:opacity-50"
                    >
                      <ExternalLink size={12} /> เปิดประวัติพนักงาน
                    </button>
                    <button
                      type="button" onClick={markApplied} disabled={busy}
                      className="px-2.5 py-1.5 rounded-lg text-[12px] text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      บันทึกแล้ว — ทำเครื่องหมายว่าเรียบร้อย
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── the confidential half ─────────────────────────────────────── */}
          <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden">
            <div className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-50 border-b border-slate-200">
              <MessageSquareQuote size={13} className="text-slate-400" />
              <span className="text-[12.5px] font-semibold text-slate-700">แบบสอบถามก่อนพ้นสภาพ</span>
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500">
                <Lock size={10} /> เฉพาะฝ่ายบุคคล
              </span>
            </div>
            <div className="px-3.5 py-3">
              {!hr ? (
                // Never an empty panel — that would read as "they answered nothing".
                <p className="text-[12.5px] text-slate-500 leading-relaxed">
                  คำตอบในแบบสอบถามนี้เปิดดูได้เฉพาะฝ่ายบุคคล เพื่อให้พนักงานตอบได้ตรงไปตรงมา
                  {r.survey_done ? ' — พนักงานตอบแบบสอบถามแล้ว' : ' — พนักงานยังไม่ได้ตอบ'}
                </p>
              ) : surveyLoading ? (
                <p className="text-[12.5px] text-slate-400"><Loader2 size={12} className="animate-spin inline" /> กำลังโหลด…</p>
              ) : surveyErr ? (
                <p className="text-[12.5px] text-rose-600">{surveyErr}</p>
              ) : !survey?.submitted_at ? (
                <p className="text-[12.5px] text-slate-400">พนักงานยังไม่ได้ตอบแบบสอบถาม</p>
              ) : (
                <div className="space-y-2.5">
                  <p className="text-[11px] text-slate-400">ตอบเมื่อ {fmtStamp(survey.submitted_at)}</p>
                  {(Array.isArray(survey.answers) ? survey.answers : []).map((a, i) => {
                    const v = Array.isArray(a.value) ? a.value.join(' · ') : String(a.value ?? '')
                    if (!v.trim()) return null
                    return (
                      <div key={a.key || i}>
                        <div className="text-[11.5px] text-slate-400">{a.label}</div>
                        <div className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">{v}</div>
                      </div>
                    )
                  })}
                  {survey.comment && (
                    <div>
                      <div className="text-[11.5px] text-slate-400">ข้อความอื่น</div>
                      <div className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">{survey.comment}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {r.status === 'pending' && (
            <>
              <label className="block">
                <span className="block text-[12px] font-medium text-slate-500 mb-1">
                  ยืนยันวันทำงานวันสุดท้าย <span className="font-normal text-slate-400">· แก้ไขได้หากตกลงกันเป็นวันอื่น</span>
                </span>
                <input type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} className={FIELD} />
              </label>
              <label className="block">
                <span className="block text-[12px] font-medium text-slate-500 mb-1">
                  บันทึกถึงพนักงาน <span className="font-normal text-slate-400">· ไม่บังคับ</span>
                </span>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={`${FIELD} resize-y`} />
              </label>
            </>
          )}

          {r.decision_note && r.status !== 'pending' && (
            <div>
              <div className="text-[11.5px] font-medium text-slate-400 mb-1">บันทึกของผู้พิจารณา</div>
              <p className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{r.decision_note}</p>
            </div>
          )}

          {err && <p className="text-[12.5px] text-rose-600">{err}</p>}
        </div>

        <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
          {r.status === 'pending' && (
            <button
              onClick={() => setConfirm('accept')} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} รับทราบการลาออก
            </button>
          )}
        </div>
      </div>

      {confirm === 'accept' && (
        <ConfirmDialog
          title="รับทราบการลาออก?"
          message={
            `วันทำงานวันสุดท้าย ${fmtDay(lastDay || r.last_working_date)}\n\n` +
            'ระบบจะไม่เขียนวันสิ้นสุดการจ้างในประวัติพนักงานให้อัตโนมัติ — ' +
            'ช่องนั้นมีผลกับเงินเดือนและตารางเวร จึงต้องเปิดประวัติพนักงานและบันทึกเอง'
          }
          confirmLabel="รับทราบ"
          onConfirm={accept}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
